import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withSize } from 'react-sizeme';

import PositionOffscreenStyles from '../styles/PositionOffscreenStyles';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import toKebabCase from '../functions/toKebabCase';
import HorizontalTabs from './HorizontalTabs';
import TabList from './TabList';
import TabListButton from './TabListButton';
import Select from './Select';


const ORIENTATIONS = {
    HORIZONTAL: 'horizontal', // horizontal orientation with NO compact form
    HORIZONTAL_DROPDOWN: 'horizontalDropdown', // horizontal orientation with dropdown compact form
    VERTICAL_ACCORDION: 'vertical', // vertical orientation with accordion compact form
    VERTICAL_DROPDOWN: 'verticalDropdown', // vertical orientation with dropdown compact form
};

const TAB_PANEL_ID_PREFIX = 'panel';
const TAB_ID_PREFIX = 'tab';

function TabContainer({
    orientation = ORIENTATIONS.HORIZONTAL,
    initialTabIndex = 0,
    activeTabIndex,
    forceRenderTabs = false,
    tabListVariant = 'dark',
    tabListSize = 'medium',
    children,
    size,
    onTabChange,
    dropdownText,
    ...otherProps
}) {
    // This MUST be an index
    const [ activeTab, setActiveTab ] = useState(activeTabIndex ?? initialTabIndex);

    const isControlled = typeof activeTabIndex === 'number';

    // Update activeTab state when the controlled prop changes
    useEffect(() => {
        if (isControlled) {
            setActiveTab(activeTabIndex);
        }
    }, [ activeTabIndex ]);

    const tabs = React.Children.map(children, child => {
        const tab = {
            title: child?.props?.title ?? '',
            subTitle: child?.props?.subTitle ?? '',
            body: child,
        };
        
        if (!tab.title || !tab.body) {
            return; // tab is invalid, skip it - tabs without subTitles are still valid!
        }

        return tab;
    });

    const tabComponentTabsProp = tabs.map(tab => ({
        title: tab.title,
        subTitle: tab.subTitle,
    }));

    // Note that subTitle isn't used in the Select. A Select component can only
    // show one label, so we're choosing to show the tab.title there only.
    const selectOptions = tabs.map((tab, index) => ({
        value: index,
        label: tab.title,
    }));

    const COMPACT_MODE_BREAKPOINT = 600;

    const isCompact = size.width < COMPACT_MODE_BREAKPOINT;
    const isHorizontal = orientation === ORIENTATIONS.HORIZONTAL || orientation === ORIENTATIONS.HORIZONTAL_DROPDOWN;

    // We want the button & list variants to match
    const tabListButtonVariant = tabListVariant;
    const tabListButtonSize = tabListSize;

    function handleTabChange(selectedIndex) {
        const index = parseInt(selectedIndex);

        if (!isControlled) {
            setActiveTab(index);
        }
        onTabChange?.(index);
    }

    return (
        <Container
            isHorizontal={isHorizontal}
            isCompact={isCompact}
            tabListSize={tabListSize}
            {...otherProps}
        >
            <Choose>
                <When condition={orientation === ORIENTATIONS.HORIZONTAL
                    || !isCompact && orientation === ORIENTATIONS.HORIZONTAL_DROPDOWN}
                >
                    <HorizontalTabs
                        className="tab-horizontal-container"
                        tabs={tabComponentTabsProp}
                        activeTabIndex={activeTab}
                        initialTabIndex={initialTabIndex}
                        onTabChange={handleTabChange}
                        tabPanelIdPrefix={TAB_PANEL_ID_PREFIX}
                        tabIdPrefix={TAB_ID_PREFIX}
                    />
                </When>

                <When condition={isCompact
                    && (orientation === ORIENTATIONS.VERTICAL_DROPDOWN
                    || orientation === ORIENTATIONS.HORIZONTAL_DROPDOWN)}
                >
                    <div className="tab-control-dropdown">
                        <label
                            className="tab-controls-label"
                            htmlFor="tabControls"
                        >
                            {dropdownText}
                        </label>

                        <Select
                            name="tabControls"
                            options={selectOptions}
                            value={activeTab}
                            onChange={handleTabChange}
                        />
                    </div>
                    <div className="dropdown-separator" />
                </When>

                <Otherwise>
                    <If condition={!isCompact}>
                        <TabList
                            className="tab-vertical-container"

                            tabs={tabComponentTabsProp}
                            activeTabIndex={activeTab}
                            onTabChange={handleTabChange}
                            tabIdPrefix={TAB_ID_PREFIX}
                            tabPanelIdPrefix={TAB_PANEL_ID_PREFIX}

                            variant={tabListVariant}
                            size={tabListSize}
                        />
                    </If>
                </Otherwise>
            </Choose>

            <For
                each="tab"
                of={tabs}
                index="index"
            >
                <If condition={!isHorizontal && isCompact && orientation === ORIENTATIONS.VERTICAL_ACCORDION}>
                    <TabListButton
                        id={`${TAB_ID_PREFIX}_${toKebabCase(tab.title)}`}
                        className="tab-list-button"
                        onClick={() => handleTabChange(index)}
                        label={tab.title}
                        isSelected={index === activeTab}
                        variant={tabListButtonVariant}
                        size={tabListButtonSize}
                    />
                </If>

                <If condition={forceRenderTabs || index === activeTab}>
                    <div
                        className="tab-panel-container"
                        role="tabpanel"
                        aria-hidden={forceRenderTabs ? activeTab !== index : false}
                        key={tab.title}
                        id={`${TAB_PANEL_ID_PREFIX}_${toKebabCase(tab.title)}`}
                        aria-labelledby={`${TAB_ID_PREFIX}_${toKebabCase(tab.title)}`}
                    >
                        {tab.body}
                    </div>
                </If>
            </For>
        </Container>
    );
}

TabContainer.propTypes = {
    orientation: PropTypes.oneOf(Object.values(ORIENTATIONS)),
    activeTabIndex: PropTypes.number, // control active tab
    initialTabIndex: PropTypes.number,
    forceRenderTabs: PropTypes.bool,
    tabListVariant: PropTypes.oneOf([
        'light',
        'dark',
    ]),
    tabListSize: PropTypes.oneOf([
        'small',
        'medium',
        'large',
    ]),
    // children: expects 0 or more Tab components, each with a title prop and children of its own
    children: PropTypes.any,
    size: PropTypes.object.isRequired,
    onTabChange: PropTypes.func,
    dropdownText: PropTypes.string, // only relevant for narrow devices and ORIENTATION.VERTICAL_DROPDOWN
};

const Container = styled.div`
    display: flex;
    flex-direction: ${props => props.isHorizontal ? 'column' : 'row'};

    ${props => !props.isHorizontal && props.isCompact && `
        flex-wrap: wrap;
    `}

    .tab-horizontal-container {
        flex-basis: auto;
    }

    .tab-vertical-container {
        ${props => props.tabListSize === 'small' && `
            flex-basis: 25%;
        `}
        ${props => props.tabListSize !== 'small' && `
            flex-basis: 35%;
        `}
        margin-right: ${Sizes.SPACING.TWO};
    }

    > .tab-control-dropdown {
        flex-basis: 100%;

        display: block;
        position: relative;
        max-width: 100%;
        margin-bottom: ${Sizes.SPACING.TWO};

        > .tab-controls-label {
            ${PositionOffscreenStyles};
        }
    }

    > .dropdown-separator {
        flex-basis: 100%;
        border-bottom: 1px solid ${Colours.STONE};
        margin-bottom: ${Sizes.SPACING.TWO_AND_A_HALF};
    }

    > .tab-list-button {
        flex-basis: 100%;
        flex-shrink: 0;
    }

    > .tab-panel-container {
        ${props => props.isHorizontal && `
            flex-basis: auto;
        `}
        ${props => !props.isHorizontal && props.isCompact && `
            flex-basis: 100%;
        `}
        ${props => !props.isHorizontal && !props.isCompact && props.tabListSize === 'small' && `
            flex-basis: 75%;
        `}
        ${props => !props.isHorizontal && !props.isCompact && props.tabListSize !== 'small' && `
            flex-basis: 65%;
        `}

        display: block;
        
        /* matches default block-margin-start on paragraphs inside RichTextBlocks */
        margin-bottom: .83em;
        
        &[aria-hidden=true] {
            display: none;
        }
    }
`;

export default withSize({ noPlaceholder: true })(TabContainer);
export { ORIENTATIONS };
